/**
 * Lista commesse — colonne estese (priority, health, invoicedAmount, contractValue)
 * + toolbar di filtro per priority, health, customer.
 * Filtri e paginazione sono persistiti nei search params dell'URL.
 */
import { createFileRoute } from '@tanstack/react-router';
import {
  Badge, Button, Card, Group, Progress, Select, SimpleGrid, Stack, Text, TextInput, ThemeIcon,
} from '@mantine/core';
import {
  IconAlertTriangle, IconCircleCheck, IconDownload, IconFolders, IconPlus, IconProgressAlert, IconSearch, IconTimeline,
  type Icon,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import {
  MantineReactTable, useMantineReactTable, type MRT_ColumnDef, type MRT_PaginationState,
} from 'mantine-react-table';
import { notifications } from '@mantine/notifications';
import { useJsonApiTable } from '@/shared/components/DataTable/useJsonApiTable';
import { PageHeader } from '@/shared/components/PageHeader';
import { fetchList } from '@/shared/api/jsonapi/client';
import { downloadCsv, getByPath, type CsvColumn } from '@/shared/lib/exportCsv';
import { formatDate, formatMoney, formatNumber } from '@/shared/lib/format';
import type { FilterClause, ListQuery } from '@/shared/api/jsonapi/types';
import { FavoriteStar } from '@/features/projects/FavoriteStar';
import { HealthBadge, PriorityBadge } from '@/features/projects/HealthBadge';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

interface Project extends Denormalized {
  type: 'project-entities';
  code?: string;
  name?: string;
  description?: string;
  customerName?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  budget?: number;
  isBillable?: boolean;
  isActive?: boolean;
  priority?: string;
  healthStatus?: string;
  invoicedAmount?: number;
  contractValue?: number;
  currency?: string;
  customer?: { id: string; type?: string; businessName?: string; name?: string };
}

interface Tenant extends Denormalized { type: 'tenants'; name?: string; businessName?: string }

import { statusColor } from '@/shared/lib/statusColors';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Bassa' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

const HEALTH_OPTIONS = [
  { value: 'green', label: 'On track' },
  { value: 'yellow', label: 'A rischio' },
  { value: 'red', label: 'Critico' },
];

const DEFAULT_PAGE_SIZE = 25;
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_ROWS = 2000;

/** Search params della lista commesse: param assente = default (URL pulito). */
interface ProjectsSearch {
  q?: string;
  priority?: string;
  health?: string;
  customer?: string;
  /** 1-based; assente = pagina 1. */
  page?: number;
  /** Assente = 25. */
  pageSize?: number;
}

export const Route = createFileRoute('/_app/projects/')({
  validateSearch: (s: Record<string, unknown>): ProjectsSearch => ({
    q: typeof s.q === 'string' && s.q.trim() ? s.q.trim() : undefined,
    priority: typeof s.priority === 'string' && PRIORITY_OPTIONS.some((o) => o.value === s.priority) ? s.priority : undefined,
    health: typeof s.health === 'string' && HEALTH_OPTIONS.some((o) => o.value === s.health) ? s.health : undefined,
    customer: typeof s.customer === 'string' && s.customer ? s.customer : undefined,
    page: typeof s.page === 'number' && s.page > 1 ? Math.floor(s.page) : undefined,
    pageSize: typeof s.pageSize === 'number' && s.pageSize > 0 && s.pageSize !== DEFAULT_PAGE_SIZE ? Math.floor(s.pageSize) : undefined,
  }),
  component: ProjectsListPage,
});

const PROJECT_CSV_COLUMNS: Array<CsvColumn<Project>> = [
  { header: 'Codice', accessor: (r) => r.code },
  { header: 'Nome', accessor: (r) => r.name },
  { header: 'Cliente', accessor: (r) => getByPath(r, 'customer.businessName') ?? getByPath(r, 'customer.name') ?? r.customerName },
  { header: 'Stato', accessor: (r) => r.status },
  { header: 'Priorità', accessor: (r) => r.priority },
  { header: 'Health', accessor: (r) => r.healthStatus },
  { header: 'Inizio', accessor: (r) => r.startDate },
  { header: 'Fine', accessor: (r) => r.endDate },
  { header: 'Fatturato', accessor: (r) => r.invoicedAmount },
  { header: 'Contratto', accessor: (r) => r.contractValue },
  { header: 'Budget', accessor: (r) => r.budget },
  { header: 'Valuta', accessor: (r) => r.currency },
];

function ProjectsListPage() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const [exporting, setExporting] = useState(false);

  const pagination: MRT_PaginationState = {
    pageIndex: (search.page ?? 1) - 1,
    pageSize: search.pageSize ?? DEFAULT_PAGE_SIZE,
  };
  const priorityF = search.priority ?? null;
  const healthF = search.health ?? null;
  const customerF = search.customer ?? null;

  const updateSearch = (patch: Partial<ProjectsSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  // Ricerca per nome: input reattivo + debounce → URL (?q=) → filtro server.
  const [qInput, setQInput] = useState(search.q ?? '');
  const [qDebounced] = useDebouncedValue(qInput.trim(), 350);
  useEffect(() => {
    if ((qDebounced || undefined) !== search.q) updateSearch({ q: qDebounced || undefined, page: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced]);

  // Filtri lato server tramite useJsonApiTable: passiamo filter via filters[]
  const filters = useMemo(() => {
    const arr: Array<{ id: string; value: unknown }> = [];
    if (search.q) arr.push({ id: 'name', value: search.q });
    if (priorityF) arr.push({ id: 'priority', value: priorityF });
    if (healthF) arr.push({ id: 'healthStatus', value: healthF });
    if (customerF) arr.push({ id: 'customer.id', value: customerF });
    return arr;
  }, [search.q, priorityF, healthF, customerF]);

  const { data, isLoading, isError } = useJsonApiTable<Project>('project-entities', {
    pagination,
    sorting: [],
    filters,
    include: ['customer'],
  });

  const tenantsQ = useQuery({
    queryKey: ['tenants-for-list-filter'],
    queryFn: () => fetchList<Tenant>('tenants', { page: { size: 100 } }),
  });
  const tenantOpts = (tenantsQ.data?.data ?? []).map((t) => ({ value: t.id, label: t.businessName ?? t.name ?? t.id }));

  const exportCsv = async () => {
    setExporting(true);
    try {
      // Ricostruisce la stessa query della tabella (filtri correnti) e scarica
      // tutte le pagine fino al cap (stesso mapping like di useJsonApiTable).
      const filter: Record<string, FilterClause> = {};
      for (const f of filters) {
        if (typeof f.value === 'string' && f.value !== '') filter[f.id] = { like: f.value };
      }

      const rows: Project[] = [];
      for (let page = 1; rows.length < EXPORT_MAX_ROWS; page++) {
        const query: ListQuery = {
          page: { number: page, size: EXPORT_PAGE_SIZE },
          filter: Object.keys(filter).length ? filter : undefined,
          include: ['customer'],
        };
        const res = await fetchList<Project>('project-entities', query);
        rows.push(...res.data);
        if (res.data.length < EXPORT_PAGE_SIZE) break;
      }
      const truncated = rows.length >= EXPORT_MAX_ROWS;
      downloadCsv(`commesse-${new Date().toISOString().slice(0, 10)}`, PROJECT_CSV_COLUMNS, rows.slice(0, EXPORT_MAX_ROWS));
      notifications.show({
        color: truncated ? 'orange' : 'green',
        message: truncated
          ? `Esportate le prime ${EXPORT_MAX_ROWS} righe (il dataset è più grande: filtra per esportare il resto).`
          : `Esportate ${rows.length} righe.`,
      });
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Errore export' });
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<MRT_ColumnDef<Project>[]>(() => ([
    {
      accessorKey: 'isFavorite',
      header: '★',
      size: 50,
      enableSorting: false,
      Cell: ({ row }) => <FavoriteStar projectId={row.original.id} />,
    },
    { accessorKey: 'code', header: 'Codice', size: 110 },
    { accessorKey: 'name', header: 'Nome' },
    {
      accessorKey: 'customerName',
      header: 'Cliente',
      Cell: ({ row }) => {
        const c = row.original.customer;
        const name = c && 'businessName' in c ? (c.businessName ?? c.name) : row.original.customerName;
        return <Text size="sm">{name ?? '—'}</Text>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Stato',
      size: 130,
      Cell: ({ cell }) => {
        const s = cell.getValue<string>();
        return s ? <Badge color={statusColor(s)}>{s}</Badge> : '—';
      },
    },
    {
      accessorKey: 'priority',
      header: 'Priorità',
      size: 110,
      Cell: ({ cell }) => <PriorityBadge value={cell.getValue<string>()} />,
    },
    {
      accessorKey: 'healthStatus',
      header: 'Health',
      size: 120,
      Cell: ({ cell }) => <HealthBadge value={cell.getValue<string>()} />,
    },
    {
      id: 'progress',
      header: 'Avanzamento',
      size: 150,
      enableSorting: false,
      Cell: ({ row }) => {
        const inv = row.original.invoicedAmount ?? 0;
        const base = row.original.contractValue ?? row.original.budget ?? 0;
        if (!base) return <Text size="xs" c="dimmed">—</Text>;
        const pct = Math.min(100, Math.round((inv / base) * 100));
        const color = pct >= 90 ? 'green' : pct >= 50 ? 'blue' : 'orange';
        return (
          <div style={{ minWidth: 120 }}>
            <Group justify="space-between" gap={4} mb={3}>
              <Text size="xs" c="dimmed">fatturato</Text>
              <Text size="xs" fw={600}>{pct}%</Text>
            </Group>
            <Progress value={pct} size="sm" color={color} radius="xl" />
          </div>
        );
      },
    },
    {
      accessorKey: 'startDate',
      header: 'Inizio',
      size: 110,
      Cell: ({ cell }) => <Text size="sm">{formatDate(cell.getValue<string>())}</Text>,
    },
    {
      accessorKey: 'endDate',
      header: 'Fine',
      size: 110,
      Cell: ({ cell }) => <Text size="sm">{formatDate(cell.getValue<string>())}</Text>,
    },
    {
      accessorKey: 'invoicedAmount',
      header: 'Fatturato',
      size: 140,
      mantineTableBodyCellProps: { align: 'right' },
      mantineTableHeadCellProps: { align: 'right' },
      Cell: ({ row }) => <Text size="sm">{formatMoney(row.original.invoicedAmount, row.original.currency ?? 'EUR')}</Text>,
    },
    {
      accessorKey: 'contractValue',
      header: 'Contratto',
      size: 140,
      mantineTableBodyCellProps: { align: 'right' },
      mantineTableHeadCellProps: { align: 'right' },
      Cell: ({ row }) => <Text size="sm">{formatMoney(row.original.contractValue, row.original.currency ?? 'EUR')}</Text>,
    },
    {
      accessorKey: 'budget',
      header: 'Budget',
      size: 130,
      mantineTableBodyCellProps: { align: 'right' },
      mantineTableHeadCellProps: { align: 'right' },
      Cell: ({ row }) => <Text size="sm">{formatMoney(row.original.budget, row.original.currency ?? 'EUR')}</Text>,
    },
  ]), []);

  const table = useMantineReactTable({
    columns,
    data: (data?.data as Project[] | undefined) ?? [],
    rowCount: data?.total ?? 0,
    state: { pagination, isLoading, showAlertBanner: isError },
    manualPagination: true,
    enableSorting: false,
    enableColumnFilters: false,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(pagination) : updater;
      updateSearch({
        page: next.pageIndex > 0 ? next.pageIndex + 1 : undefined,
        pageSize: next.pageSize !== DEFAULT_PAGE_SIZE ? next.pageSize : undefined,
      });
    },
    initialState: { density: 'md' },
    paginationDisplayMode: 'pages',
    enableStickyHeader: true,
    mantinePaperProps: { withBorder: true, radius: 'lg', shadow: 'sm' },
    mantineTableProps: { highlightOnHover: true, striped: 'odd' },
    mantineTableHeadCellProps: { style: { fontWeight: 600 } },
    renderTopToolbarCustomActions: () => (
      <Button
        size="xs"
        variant="default"
        leftSection={<IconDownload size={14} />}
        loading={exporting}
        disabled={isLoading || (data?.total ?? 0) === 0}
        onClick={() => void exportCsv()}
      >
        Esporta CSV
      </Button>
    ),
    mantineToolbarAlertBannerProps: isError ? { color: 'red', children: 'Errore caricamento' } : undefined,
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => void navigate({ to: `/projects/${row.original.id}` }),
      style: { cursor: 'pointer' },
    }),
    renderEmptyRowsFallback: () => <Text c="dimmed" ta="center" py="xl">Nessuna commessa</Text>,
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title="Commesse"
        description="Gestione progetti, budget e team"
        actions={
          <>
            <Button variant="light" leftSection={<IconTimeline size={16} />} onClick={() => void navigate({ to: '/projects/gantt' })}>
              Gantt overview
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={() => void navigate({ to: '/projects/new' })}>
              Nuova commessa
            </Button>
          </>
        }
      />

      <ProjectsKpis />

      <Card withBorder padding="sm" radius="lg">
        <Group gap="sm" wrap="wrap" align="flex-end">
          <TextInput
            placeholder="Cerca per nome…"
            leftSection={<IconSearch size={16} />}
            value={qInput}
            onChange={(e) => setQInput(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          <Select
            placeholder="Priorità"
            data={PRIORITY_OPTIONS}
            value={priorityF}
            onChange={(v) => updateSearch({ priority: v ?? undefined, page: undefined })}
            clearable
            w={170}
          />
          <Select
            placeholder="Health"
            data={HEALTH_OPTIONS}
            value={healthF}
            onChange={(v) => updateSearch({ health: v ?? undefined, page: undefined })}
            clearable
            w={170}
          />
          <Select
            placeholder="Cliente"
            data={tenantOpts}
            value={customerF}
            onChange={(v) => updateSearch({ customer: v ?? undefined, page: undefined })}
            clearable
            searchable
            w={220}
          />
        </Group>
      </Card>

      <MantineReactTable table={table} />
    </Stack>
  );
}

/** Conteggi globali (page.size:1 → meta.total). Resilienti: in errore mostrano "—". */
function useProjectCount(key: string, filter?: Record<string, FilterClause>) {
  return useQuery({
    queryKey: ['projects-kpi', key],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await fetchList<Project>('project-entities', { page: { size: 1 }, filter });
      return res.total;
    },
  });
}

function ProjectsKpis() {
  const total = useProjectCount('total');
  const green = useProjectCount('green', { healthStatus: { eq: 'green' } });
  const yellow = useProjectCount('yellow', { healthStatus: { eq: 'yellow' } });
  const red = useProjectCount('red', { healthStatus: { eq: 'red' } });

  return (
    <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
      <KpiCard label="Totale commesse" icon={IconFolders} color="blue" q={total} />
      <KpiCard label="On track" icon={IconCircleCheck} color="green" q={green} />
      <KpiCard label="A rischio" icon={IconProgressAlert} color="yellow" q={yellow} />
      <KpiCard label="Critiche" icon={IconAlertTriangle} color="red" q={red} />
    </SimpleGrid>
  );
}

function KpiCard({ label, icon: Icon, color, q }: {
  label: string;
  icon: Icon;
  color: string;
  q: { data?: number; isLoading: boolean; isError: boolean };
}) {
  return (
    <Card withBorder padding="md" radius="lg">
      <Group justify="space-between" wrap="nowrap">
        <div style={{ minWidth: 0 }}>
          <Text size="xs" c="dimmed">{label}</Text>
          <Text fz={26} fw={800} lh={1.1} c={color === 'blue' ? undefined : `${color}.7`}>
            {q.isLoading ? '…' : q.isError ? '—' : formatNumber(q.data ?? 0)}
          </Text>
        </div>
        <ThemeIcon variant="light" color={color} size={40} radius="md"><Icon size={22} /></ThemeIcon>
      </Group>
    </Card>
  );
}
